import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { appsApi, errMsg, notifyError } from "@/lib/appsApi";

const KEY = "apps-active-tab";

export const useTabs = () => {
  const [tabs, setTabs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActiveRaw] = useState(() => localStorage.getItem(KEY) || "");

  const setActive = (id) => {
    setActiveRaw(id);
    localStorage.setItem(KEY, id);
  };

  const reload = useCallback(() => appsApi.get("/tabs")
    .then((r) => setTabs(Array.isArray(r?.data) ? r.data : []))
    .catch((err) => notifyError(errMsg(err, "Loading the tabs")))
    .finally(() => setLoaded(true)), []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.id === active)) setActive(tabs[0].id);
  }, [tabs, active]);

  const run = async (what, fn) => {
    try {
      await fn();
      await reload();
      return true;
    } catch (err) {
      notifyError(errMsg(err, what));
      return false;
    }
  };

  const add = (name) => run("Adding the tab", async () => {
    const r = await appsApi.post("/admin/tabs", { name });
    if (r?.data?.id) setActive(r.data.id);
    toast.success(`Tab "${r?.data?.name ?? name}" added`);
  });
  const rename = (id, name) => run("Renaming the tab", async () => {
    await appsApi.patch(`/admin/tabs/${id}`, { name });
    toast.success("Tab renamed");
  });
  const remove = (tab) => {
    if (tab.file_count) {
      notifyError(`"${tab.name}" still has ${tab.file_count} file(s). Move them to another tab (or delete them) first, then remove the tab.`);
      return Promise.resolve(false);
    }
    if (!window.confirm(`Remove the "${tab.name}" tab? Testers who had it enabled will no longer see it.`)) return Promise.resolve(false);
    return run("Removing the tab", async () => {
      await appsApi.delete(`/admin/tabs/${tab.id}`);
      toast.success(`Tab "${tab.name}" removed`);
    });
  };

  return { tabs, loaded, active, setActive, reload, add, rename, remove };
};
